// shopifyCart.js
// Cart API service for ParksPass. Replaces deprecated checkout.create flow.
// Persists cartId in localStorage so users don't lose their cart on refresh.

const SHOPIFY_DOMAIN = "parkspassutah.myshopify.com";
const STOREFRONT_TOKEN = "a245460c3b59913598a8b8b1f049af40"; // move to env var
const API_VERSION = "2025-01";
const STORAGE_KEY = "parkspass_cart_id";

const endpoint = `https://${SHOPIFY_DOMAIN}/api/${API_VERSION}/graphql.json`;

async function gql(query, variables = {}) {
  const res = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": STOREFRONT_TOKEN,
    },
    body: JSON.stringify({ query, variables }),
  });
  const json = await res.json();
  if (json.errors) {
    console.error("Shopify GraphQL errors:", json.errors);
    throw new Error(json.errors[0]?.message || "GraphQL error");
  }
  return json.data;
}

// ---- One line per plate-required pass ---------------------------------
// Shopify merges lines with the same variant + attributes into one line with
// a higher quantity. A day-use pass needs a plate per vehicle, so every
// plate-required pass is stamped with a unique hidden _lineKey attribute to
// keep it on its own line (quantity 1) with its own plate input.
const PLATE_ATTR_RE = /^License Plate/;

function attrsRequirePlate(attributes) {
  return (attributes || []).some(
    (a) => a.key === "_requiresPlate" && a.value === "true"
  );
}

export function lineRequiresPlate(line) {
  return attrsRequirePlate(line?.attributes);
}

function newLineKey() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// Build the CartLineInput list for an add. Plate-required passes are split
// into `quantity` separate lines, each with a fresh _lineKey.
function buildLineInputs({ merchandiseId, quantity = 1, attributes = [] }) {
  if (!attrsRequirePlate(attributes)) {
    return [{ merchandiseId, quantity, attributes }];
  }
  const base = attributes.filter((a) => a.key !== "_lineKey");
  const lines = [];
  for (let i = 0; i < Math.max(1, quantity); i++) {
    lines.push({
      merchandiseId,
      quantity: 1,
      attributes: [...base, { key: "_lineKey", value: newLineKey() }],
    });
  }
  return lines;
}

// Common cart fields we want back from every mutation
const CART_FRAGMENT = `
  fragment CartFields on Cart {
    id
    checkoutUrl
    totalQuantity
    attributes { key value }
    cost {
      subtotalAmount { amount currencyCode }
      totalAmount { amount currencyCode }
    }
    lines(first: 50) {
      nodes {
        id
        quantity
        attributes { key value }
        merchandise {
          ... on ProductVariant {
            id
            title
            price { amount currencyCode }
            image { url altText }
            product { title handle }
          }
        }
      }
    }
  }
`;

export const shopifyCart = {
  cart: null,

  getStoredId() {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  },

  setStoredId(id) {
    try {
      if (id) localStorage.setItem(STORAGE_KEY, id);
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  },

  // Load existing cart from localStorage, or return null if expired/missing
  async load() {
    const id = this.getStoredId();
    if (!id) return null;
    const query = `
      ${CART_FRAGMENT}
      query GetCart($id: ID!) {
        cart(id: $id) { ...CartFields }
      }
    `;
    try {
      const data = await gql(query, { id });
      if (data.cart) {
        this.cart = data.cart;
        // Best effort: a failure here must not throw away the cart.
        try {
          await this.splitPlateLines();
        } catch (e) {
          console.warn("Failed to split legacy plate lines:", e);
        }
        return this.cart;
      }
      // Cart expired/null — clear stored id
      this.setStoredId(null);
      return null;
    } catch (e) {
      console.warn("Failed to load cart:", e);
      this.setStoredId(null);
      return null;
    }
  },

  // Create a new cart with optional initial lines and cart-level attributes
  async create({ lines = [], attributes = [] } = {}) {
    const mutation = `
      ${CART_FRAGMENT}
      mutation CartCreate($input: CartInput!) {
        cartCreate(input: $input) {
          cart { ...CartFields }
          userErrors { field message }
        }
      }
    `;
    const data = await gql(mutation, {
      input: { lines: lines.flatMap(buildLineInputs), attributes },
    });
    if (data.cartCreate.userErrors.length) {
      throw new Error(data.cartCreate.userErrors[0].message);
    }
    this.cart = data.cartCreate.cart;
    this.setStoredId(this.cart.id);
    return this.cart;
  },

  // Add a single line (with optional line-level attributes like license plate)
  async addLine({ merchandiseId, quantity = 1, attributes = [] }) {
    // Lazy-init: if no cart yet, create one with this line
    if (!this.cart) {
      await this.load();
    }
    if (!this.cart) {
      return this.create({
        lines: [{ merchandiseId, quantity, attributes }],
      });
    }

    const mutation = `
      ${CART_FRAGMENT}
      mutation CartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
        cartLinesAdd(cartId: $cartId, lines: $lines) {
          cart { ...CartFields }
          userErrors { field message }
        }
      }
    `;
    const data = await gql(mutation, {
      cartId: this.cart.id,
      lines: buildLineInputs({ merchandiseId, quantity, attributes }),
    });
    if (data.cartLinesAdd.userErrors.length) {
      throw new Error(data.cartLinesAdd.userErrors[0].message);
    }
    this.cart = data.cartLinesAdd.cart;
    return this.cart;
  },

  // Merge newAttrs into existing cart-level attributes (replaces by key, appends new)
  async updateAttributes(newAttrs) {
    const existing = this.cart?.attributes || [];
    const merged = [...existing];
    for (const attr of newAttrs) {
      const idx = merged.findIndex((a) => a.key === attr.key);
      if (idx >= 0) merged[idx] = attr;
      else merged.push(attr);
    }
    const mutation = `
      ${CART_FRAGMENT}
      mutation CartAttributesUpdate($cartId: ID!, $attributes: [AttributeInput!]!) {
        cartAttributesUpdate(cartId: $cartId, attributes: $attributes) {
          cart { ...CartFields }
          userErrors { field message }
        }
      }
    `;
    const data = await gql(mutation, {
      cartId: this.cart.id,
      attributes: merged,
    });
    if (data.cartAttributesUpdate.userErrors.length) {
      throw new Error(data.cartAttributesUpdate.userErrors[0].message);
    }
    this.cart = data.cartAttributesUpdate.cart;
    return this.cart;
  },

  async updateLineAttributes(lineId, attributes) {
    const mutation = `
      ${CART_FRAGMENT}
      mutation CartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
        cartLinesUpdate(cartId: $cartId, lines: $lines) {
          cart { ...CartFields }
          userErrors { field message }
        }
      }
    `;
    const data = await gql(mutation, {
      cartId: this.cart.id,
      lines: [{ id: lineId, attributes }],
    });
    if (data.cartLinesUpdate.userErrors.length) {
      throw new Error(data.cartLinesUpdate.userErrors[0].message);
    }
    this.cart = data.cartLinesUpdate.cart;
    return this.cart;
  },

  async updateLineQuantity(lineId, quantity) {
    if (quantity <= 0) return this.removeLine(lineId);

    // Plate-required passes live one per line. "Increase" adds another copy
    // (with its own plate input) instead of raising this line's quantity.
    const line = this.cart?.lines?.nodes?.find((l) => l.id === lineId);
    if (line && lineRequiresPlate(line) && quantity > line.quantity) {
      return this.addLine({
        merchandiseId: line.merchandise.id,
        quantity: quantity - line.quantity,
        attributes: line.attributes.filter(
          (a) => !PLATE_ATTR_RE.test(a.key) && a.key !== "_lineKey"
        ),
      });
    }

    const mutation = `
      ${CART_FRAGMENT}
      mutation CartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
        cartLinesUpdate(cartId: $cartId, lines: $lines) {
          cart { ...CartFields }
          userErrors { field message }
        }
      }
    `;
    const data = await gql(mutation, {
      cartId: this.cart.id,
      lines: [{ id: lineId, quantity }],
    });
    if (data.cartLinesUpdate.userErrors.length) {
      throw new Error(data.cartLinesUpdate.userErrors[0].message);
    }
    this.cart = data.cartLinesUpdate.cart;
    return this.cart;
  },

  // Legacy carts (created before one-line-per-pass) may hold a plate-required
  // line with quantity > 1. Reduce it to 1 and add the rest as separate lines.
  async splitPlateLines() {
    const lines = this.cart?.lines?.nodes || [];
    const oversized = lines.filter(
      (l) => lineRequiresPlate(l) && l.quantity > 1
    );
    for (const line of oversized) {
      const extra = line.quantity - 1;
      await this.updateLineQuantity(line.id, 1);
      await this.addLine({
        merchandiseId: line.merchandise.id,
        quantity: extra,
        attributes: line.attributes.filter(
          (a) => !PLATE_ATTR_RE.test(a.key) && a.key !== "_lineKey"
        ),
      });
    }
    return this.cart;
  },

  async removeLine(lineId) {
    const mutation = `
      ${CART_FRAGMENT}
      mutation CartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
        cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
          cart { ...CartFields }
          userErrors { field message }
        }
      }
    `;
    const data = await gql(mutation, {
      cartId: this.cart.id,
      lineIds: [lineId],
    });
    if (data.cartLinesRemove.userErrors.length) {
      throw new Error(data.cartLinesRemove.userErrors[0].message);
    }
    this.cart = data.cartLinesRemove.cart;
    return this.cart;
  },

  // GraphQL query to fetch a collection's products + variants for our custom UI
  async fetchCollection(collectionId) {
    const isGid = String(collectionId).startsWith("gid://");
    const gid = isGid
      ? collectionId
      : `gid://shopify/Collection/${collectionId}`;

    const query = `
      query CollectionProducts($id: ID!, $first: Int!) {
        collection(id: $id) {
          title
          products(first: $first) {
            nodes {
              id
              title
              handle
              description
              featuredImage { url altText }
              variants(first: 50) {
                nodes {
                  id
                  title
                  availableForSale
                  price { amount currencyCode }
                  image { url altText }
                }
              }
            }
          }
        }
      }
    `;
    const data = await gql(query, { id: gid, first: 50 });
    return data.collection;
  },

  // Single product by numeric id or gid. Returns the same product shape as
  // fetchCollection's nodes so CustomBuyButton can render it directly.
  async fetchProduct(productId) {
    const isGid = String(productId).startsWith("gid://");
    const gid = isGid ? productId : `gid://shopify/Product/${productId}`;

    const query = `
      query ProductById($id: ID!) {
        product(id: $id) {
          id
          title
          handle
          description
          featuredImage { url altText }
          variants(first: 50) {
            nodes {
              id
              title
              availableForSale
              price { amount currencyCode }
              image { url altText }
            }
          }
        }
      }
    `;
    const data = await gql(query, { id: gid });
    return data.product;
  },
};
